/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
/**
 * Component that creates a 3D tip
 */

import React from "react";
import PropTypes from "prop-types";
import Text from "../Text";
import BackgroundPanel from "../BackgroundPanel";
import Button from "../Button";
import OverlayPanel from "../OverlayPanel";
import ImageButton from "../ImageButton";

function Tip({ data, setPosition, aframeClasses }) {
    const offset =
        data.width > data.height ? data.height * 0.1 : data.width * 0.1;
    const drawButtons = () => {
        let xPosition = data.width / 2;
        return [...data.buttons].reverse().map((elem, id) => {
            const button = (
                <Button
                    key={id}
                    disabled={elem.disabled}
                    text={elem.text}
                    width={elem.width}
                    height={data.height * 0.1}
                    type={elem.type}
                    onClickHandler={
                        elem.onClickHandler === "next"
                            ? () => setPosition((old) => old + 1)
                            : elem.onClickHandler === "back"
                            ? () => setPosition((old) => old - 1)
                            : elem.onClickHandler
                    }
                    aframeClasses={aframeClasses}
                    textSizeDecimal={50 / data.width}
                    position={`${xPosition - offset - elem.width / 2} ${
                        -data.height * 0.5 + offset + data.height * 0.05
                    } 0.0001`}
                />
            );
            xPosition -= elem.width + offset;
            return button;
        });
    };
    const drawTriangle = () => {
        const triangleWidth = data.width * 0.1;
        let position, rotation;
        switch (data.pointer.side) {
            case "bottom":
                position = {
                    x: -data.width / 2 + data.width * data.pointer.percentage,
                    y: -data.height / 2
                };
                if (position.x < -data.width / 2 + triangleWidth / 2)
                    position.x = -data.width / 2 + triangleWidth / 2;
                if (position.x > data.width / 2 - triangleWidth / 2)
                    position.x = data.width / 2 - triangleWidth / 2;
                rotation = "0 0 180";
                break;
            case "top":
                position = {
                    x: -data.width / 2 + data.width * data.pointer.percentage,
                    y: data.height / 2
                };
                if (position.x < -data.width / 2 + triangleWidth / 2)
                    position.x = -data.width / 2 + triangleWidth / 2;
                if (position.x > data.width / 2 - triangleWidth / 2)
                    position.x = data.width / 2 - triangleWidth / 2;
                rotation = "0 0 0";
                break;
            case "left":
                position = {
                    x: -data.width / 2,
                    y: -data.height / 2 + data.height * data.pointer.percentage
                };
                if (position.y < -data.height / 2 + triangleWidth / 2)
                    position.y = -data.height / 2 + triangleWidth / 2;
                if (position.y > data.height / 2 - triangleWidth / 2)
                    position.y = data.height / 2 - triangleWidth / 2;
                rotation = "0 0 90";
                break;
            case "right":
                position = {
                    x: data.width / 2,
                    y: -data.height / 2 + data.height * data.pointer.percentage
                };
                if (position.y < -data.height / 2 + triangleWidth / 2)
                    position.y = -data.height / 2 + triangleWidth / 2;
                if (position.y > data.height / 2 - triangleWidth / 2)
                    position.y = data.height / 2 - triangleWidth / 2;
                rotation = "0 0 -90";
                break;
        }

        return (
            <a-triangle
                color="#121212"
                vertex-a={`0 ${data.height * 0.1} 0`}
                vertex-b={`${-triangleWidth / 2} 0 0`}
                vertex-c={`${triangleWidth / 2} 0 0`}
                position={`${position.x} ${position.y} 0`}
                rotation={rotation}
            />
        );
    };
    const drawOverlays = () => {
        if (!Array.isArray(data.overlay)) data.overlay = [data.overlay];
        return data.overlay.map((elem, id) => {
            return (
                <OverlayPanel
                    key={id}
                    width={elem.width}
                    height={elem.height}
                    position={elem.position}
                    aframeClasses={aframeClasses}
                />
            );
        });
    };
    return (
        <a-entity class="Tip">
            <BackgroundPanel
                width={data.width}
                height={data.height}
                position={data.position}
            >
                {data.close && (
                    <ImageButton
                        src="#CloseWhite"
                        width={offset}
                        height={offset}
                        aframeClasses={aframeClasses}
                        onClickHandler={data.close.onClickHandler}
                        position={`${data.width / 2 - offset * 1.5} ${
                            data.height / 2 - offset * 1.5
                        } 0`}
                    />
                )}
                {drawTriangle()}
                <Text
                    text={data.text}
                    width={data.width - offset * 2}
                    baseline="top"
                    position={`${-data.width * 0.5 + offset} ${
                        data.height * 0.5 - offset * (data.close ? 3 : 1)
                    } 0`}
                    sizeDecimal={data.textSizeDecimal || 45 / data.width}
                />
                {drawButtons()}
            </BackgroundPanel>
            {drawOverlays()}
        </a-entity>
    );
}

Tip.propTypes = {
    data: PropTypes.object.isRequired,
    setPosition: PropTypes.func.isRequired
};

export default Tip;
